import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import {
  Key,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Save,
  Copy,
  Check,
  RefreshCw,
  Shield,
  Clock,
  AlertTriangle,
  Eye,
  EyeOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Available scopes - Now using translations
const getScopeInfo = (t) => [
  { code: 'webhooks', name: t('apiTokens.scopes.webhooks.name'), description: t('apiTokens.scopes.webhooks.description') },
  { code: 'orders', name: t('apiTokens.scopes.orders.name'), description: t('apiTokens.scopes.orders.description') },
  { code: 'patients', name: t('apiTokens.scopes.patients.name'), description: t('apiTokens.scopes.patients.description') },
  { code: 'specialists', name: t('apiTokens.scopes.specialists.name'), description: t('apiTokens.scopes.specialists.description') },
  { code: 'workflows', name: t('apiTokens.scopes.workflows.name'), description: t('apiTokens.scopes.workflows.description') },
  { code: 'full_access', name: t('apiTokens.scopes.full_access.name'), description: t('apiTokens.scopes.full_access.description') }
];

export const APITokensPage = () => {
  const { t } = useTranslation('security');
  const AVAILABLE_SCOPES = getScopeInfo(t);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingToken, setEditingToken] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [newTokenValue, setNewTokenValue] = useState(null);
  const [showTokenDialog, setShowTokenDialog] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    scopes: [],
    expires_in_days: null
  });

  useEffect(() => {
    fetchTokens();
  }, [page, activeFilter]);

  const fetchTokens = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      if (activeFilter !== 'all') {
        params.append('is_active', activeFilter === 'active' ? 'true' : 'false');
      }
      
      const response = await axios.get(`${API_URL}/api/api-tokens?${params}`);
      setTokens(response.data.data);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      toast.error(t('apiTokens.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const openForm = (token = null) => {
    if (token) {
      setEditingToken(token);
      setFormData({
        name: token.name,
        description: token.description || '',
        scopes: token.scopes || [],
        expires_in_days: null
      });
    } else {
      setEditingToken(null);
      setFormData({
        name: '',
        description: '',
        scopes: ['webhooks'],
        expires_in_days: null
      });
    }
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error(t('apiTokens.messages.nameRequired'));
      return;
    }
    
    if (formData.scopes.length === 0) {
      toast.error(t('apiTokens.messages.scopeRequired'));
      return;
    }
    
    setFormLoading(true);
    try {
      if (editingToken) {
        await axios.put(`${API_URL}/api/api-tokens/${editingToken.id}`, {
          name: formData.name,
          description: formData.description,
          scopes: formData.scopes
        });
        toast.success(t('apiTokens.messages.updated'));
        setShowForm(false);
        fetchTokens();
      } else {
        const response = await axios.post(`${API_URL}/api/api-tokens`, formData);
        setNewTokenValue(response.data.token);
        setShowForm(false);
        setShowTokenDialog(true);
        fetchTokens();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('apiTokens.messages.saveError'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (tokenId) => {
    if (!window.confirm(t('apiTokens.messages.confirmDelete'))) return;
    
    try {
      await axios.delete(`${API_URL}/api/api-tokens/${tokenId}`);
      toast.success(t('apiTokens.messages.deleted'));
      fetchTokens();
    } catch (error) {
      toast.error(t('apiTokens.messages.deleteError'));
    }
  };

  const handleRegenerate = async (tokenId) => {
    if (!window.confirm(t('apiTokens.messages.confirmRegenerate'))) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/api-tokens/${tokenId}/regenerate`);
      setNewTokenValue(response.data.token);
      setShowTokenDialog(true);
      fetchTokens();
    } catch (error) {
      toast.error(t('apiTokens.messages.regenerateError'));
    }
  };

  const toggleScope = (scopeCode) => {
    if (formData.scopes.includes(scopeCode)) {
      // If removing full_access, keep it if it's the only scope
      if (scopeCode === 'full_access' && formData.scopes.length === 1) return;
      setFormData({
        ...formData,
        scopes: formData.scopes.filter(s => s !== scopeCode)
      });
    } else {
      // If adding full_access, remove other scopes
      if (scopeCode === 'full_access') {
        setFormData({
          ...formData,
          scopes: ['full_access']
        });
      } else {
        // If adding a specific scope, remove full_access
        setFormData({
          ...formData,
          scopes: [...formData.scopes.filter(s => s !== 'full_access'), scopeCode]
        });
      }
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(t('apiTokens.messages.copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(t('apiTokens.messages.copyError'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('apiTokens.labels.never');
    return new Date(dateString).toLocaleString();
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <div className="space-y-6" data-testid="api-tokens-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('apiTokens.title')}</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">{t('apiTokens.subtitle')}</p>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900 dark:text-blue-100 font-medium">{t('apiTokens.infoCard.title')}</p>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                {t('apiTokens.infoCard.description')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('apiTokens.searchPlaceholder')}
              className="pl-10"
              data-testid="token-search"
            />
          </div>
        </div>
        <Select value={activeFilter} onValueChange={(v) => { setActiveFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[150px]" data-testid="status-filter">
            <SelectValue placeholder={t('apiTokens.filters.status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('apiTokens.filters.all')}</SelectItem>
            <SelectItem value="active">{t('apiTokens.filters.active')}</SelectItem>
            <SelectItem value="inactive">{t('apiTokens.filters.inactive')}</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => openForm()} className="bg-[#E63946] hover:bg-[#d32836]" data-testid="new-token-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t('apiTokens.newToken')}
        </Button>
      </div>

      {/* Tokens List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
        </div>
      ) : tokens.length > 0 ? (
        <div className="space-y-3">
          {tokens.map(token => (
            <Card key={token.id} className={`${!token.is_active || isExpired(token.expires_at) ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center">
                      <Key className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{token.name}</p>
                        {!token.is_active && <Badge variant="secondary">{t('apiTokens.status.inactive')}</Badge>}
                        {isExpired(token.expires_at) && <Badge variant="destructive">{t('apiTokens.status.expired')}</Badge>}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">{token.token_prefix}</p>
                      {token.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{token.description}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-right text-sm">
                      <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                        <Clock className="h-3 w-3" />
                        <span>{t('apiTokens.labels.lastUsed')}: {formatDate(token.last_used_at)}</span>
                      </div>
                      {token.expires_at && (
                        <div className="text-gray-400 mt-1">
                          {t('apiTokens.labels.expires')}: {formatDate(token.expires_at)}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-1 max-w-[200px]">
                      {token.scopes?.map(scope => (
                        <Badge key={scope} variant="outline" className="text-xs">
                          {scope}
                        </Badge>
                      ))}
                    </div>
                    
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleRegenerate(token.id)} title="Regenerate">
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openForm(token)} title="Edit">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(token.id)} title="Delete">
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Key className="h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
          <p>{t('apiTokens.noTokens')}</p>
          <p className="text-sm mt-2">{t('apiTokens.noTokensSubtitle')}</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="flex items-center px-4 text-sm">
            {t('apiTokens.page', { current: page, total: totalPages })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Token Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingToken ? t('apiTokens.form.editTitle') : t('apiTokens.form.title')}</DialogTitle>
            <DialogDescription>
              {editingToken 
                ? t('apiTokens.form.editDescription')
                : t('apiTokens.form.description')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('apiTokens.form.name')} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder={t('apiTokens.form.namePlaceholder')}
                data-testid="token-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('apiTokens.form.tokenDescription')}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder={t('apiTokens.form.descriptionPlaceholder')}
                rows={2}
                data-testid="token-description"
              />
            </div>
            
            {!editingToken && (
              <div className="space-y-2">
                <Label>{t('apiTokens.form.expiration')}</Label>
                <Select
                  value={formData.expires_in_days?.toString() || 'never'}
                  onValueChange={(v) => setFormData({...formData, expires_in_days: v === 'never' ? null : parseInt(v)})}
                >
                  <SelectTrigger data-testid="token-expiration">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="never">{t('apiTokens.form.neverExpires')}</SelectItem>
                    <SelectItem value="30">{t('apiTokens.form.days30')}</SelectItem>
                    <SelectItem value="90">{t('apiTokens.form.days90')}</SelectItem>
                    <SelectItem value="180">{t('apiTokens.form.days180')}</SelectItem>
                    <SelectItem value="365">{t('apiTokens.form.year1')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-3">
              <Label>{t('apiTokens.form.permissions')} *</Label>
              <div className="border rounded-lg divide-y dark:divide-gray-700">
                {AVAILABLE_SCOPES.map(scope => (
                  <div
                    key={scope.code}
                    className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
                      formData.scopes.includes(scope.code) ? 'bg-purple-50 dark:bg-purple-900/30' : ''
                    }`}
                    onClick={() => toggleScope(scope.code)}
                  >
                    <Checkbox
                      checked={formData.scopes.includes(scope.code)}
                      onCheckedChange={() => toggleScope(scope.code)}
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{scope.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{scope.description}</p>
                    </div>
                    {scope.code === 'full_access' && (
                      <Badge variant="destructive" className="text-xs">Admin</Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={formLoading}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={formLoading} data-testid="save-token-btn">
              {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {editingToken ? t('common.update') : t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Token Display Dialog */}
      <Dialog open={showTokenDialog} onOpenChange={(open) => {
        if (!open) {
          setShowTokenDialog(false);
          setNewTokenValue(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              {t('apiTokens.successDialog.title')}
            </DialogTitle>
          </DialogHeader>
          
          <Alert className="bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              <strong>Important:</strong> {t('apiTokens.successDialog.warning')}
            </AlertDescription>
          </Alert>
          
          <div className="space-y-3">
            <Label>{t('apiTokens.successDialog.label')}</Label>
            <div className="flex gap-2">
              <Input
                value={newTokenValue || ''}
                readOnly
                className="font-mono text-sm bg-gray-50 dark:bg-gray-800"
                data-testid="new-token-value"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(newTokenValue)}
                className="shrink-0"
                data-testid="copy-token-btn"
              >
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">{t('apiTokens.successDialog.howToUse')}</p>
            <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <p>{t('apiTokens.successDialog.headerInstruction')}</p>
              <code className="block bg-gray-100 dark:bg-gray-700 p-2 rounded text-xs font-mono">
                Authorization: Bearer {newTokenValue?.substring(0, 20)}...
              </code>
              <p>{t('apiTokens.successDialog.orUseKey')}</p>
              <code className="block bg-gray-100 dark:bg-gray-700 p-2 rounded text-xs font-mono">
                X-API-Key: {newTokenValue?.substring(0, 20)}...
              </code>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => {
              setShowTokenDialog(false);
              setNewTokenValue(null);
            }}>
              {t('apiTokens.successDialog.understood')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default APITokensPage;
